import { Alert, Button, Card, InputNumber, Skeleton, Spin, Table, message } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { getBilling, getRates, saveInvoice, saveRates } from './api'
import BillingFilters, { defaultRange } from './BillingFilters'
import ChargeBreakdown from './ChargeBreakdown'
import { DailyChart, Donut, Funnel } from './charts'
import { COLORS, dmy, eur, marginPct, num } from './format'
import Glossary, { Term } from './Glossary'
import MoneyFlow from './MoneyFlow'
import ReconciliationCard, { fullMonthOf } from './ReconciliationCard'
import UserDetailDrawer from './UserDetailDrawer'

const Kpi = ({ label, value, sub, color }) => (
  <Card size="small" style={{ flex: '1 1 180px' }}>
    <div style={{ color: '#8c8c8c', fontSize: 12 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</div>}
  </Card>
)

const numCol = (title, dataIndex) => ({
  title,
  dataIndex,
  key: dataIndex,
  align: 'right',
  render: num,
  sorter: (a, b) => (a[dataIndex] || 0) - (b[dataIndex] || 0)
})

const Billing = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [rows, setRows] = useState([])
  const [totals, setTotals] = useState(null)
  const [computedAt, setComputedAt] = useState(null)
  const [rates, setRates] = useState({ basico: 0.021, simple: 0.05, conversacional: 0.05 })
  const [ratesDraft, setRatesDraft] = useState(null)
  const [savingRates, setSavingRates] = useState(false)
  const [invoices, setInvoices] = useState({})
  // `draft` = lo que está en el selector; `applied` = lo que se está mostrando.
  const [draft, setDraft] = useState(defaultRange())
  const [applied, setApplied] = useState(draft)
  const [detailUser, setDetailUser] = useState(null)
  // Solo vale la última petición: una respuesta lenta de un periodo anterior no pisa la actual.
  const reqId = useRef(0)

  const loadRates = async () => {
    try {
      const r = await getRates()
      setRates(r)
      setInvoices(r.invoices || {})
      setRatesDraft({ basico: r.basico, simple: r.simple, conversacional: r.conversacional })
    } catch (err) {
      console.log('error tarifas', err)
    }
  }

  // `refresh` fuerza el recálculo en el servidor (si no, devuelve lo ya calculado y guardado).
  const loadBilling = async (range, refresh = false) => {
    const id = ++reqId.current
    setLoading(true)
    setError(false)
    try {
      const res = await getBilling({ from: range.from, to: range.to, refresh: refresh ? '1' : undefined })
      if (id !== reqId.current) return
      setRows(res.users || [])
      setTotals(res.totals || null)
      if (res.rates) setRates(res.rates)
      setApplied(range)
      setComputedAt(res.computedAt ? new Date(res.computedAt) : new Date())
    } catch (err) {
      if (id !== reqId.current) return
      console.log('error billing', err)
      setError(true)
      message.error('No se pudo cargar la facturación')
    }
    if (id === reqId.current) setLoading(false)
  }

  useEffect(() => {
    loadRates()
    loadBilling(applied)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSaveRates = async () => {
    setSavingRates(true)
    try {
      await saveRates(ratesDraft)
      message.success('Tarifas Orange actualizadas')
      setRates(ratesDraft)
      loadBilling(applied)
    } catch (err) {
      console.log('error guardando tarifas', err)
      message.error('No se pudieron guardar las tarifas')
    }
    setSavingRates(false)
  }

  const columns = [
    {
      title: 'Cliente',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (v) => <span style={{ color: '#4F46E5', fontWeight: 500 }}>{v}</span>
    },
    {
      title: <Term id="contactados">Teléfonos</Term>,
      key: 'contacted',
      align: 'right',
      render: (_, r) => num(r.phoneFunnel ? r.phoneFunnel.contacted : 0),
      sorter: (a, b) => (a.phoneFunnel?.contacted || 0) - (b.phoneFunnel?.contacted || 0)
    },
    numCol(<Term id="simple" />, 'simple'),
    numCol(<Term id="basico" />, 'basico'),
    numCol(<Term id="conversacion" />, 'firstSessions'),
    numCol(<Term id="extra" />, 'extraSessions'),
    numCol(<Term id="incluido" />, 'bundled'),
    {
      title: <Term id="coste" />,
      dataIndex: 'cost',
      key: 'cost',
      align: 'right',
      render: (v) => eur(v),
      sorter: (a, b) => a.cost - b.cost
    },
    {
      title: <Term id="cobrado">Cobrado</Term>,
      dataIndex: 'retailRcs',
      key: 'retailRcs',
      align: 'right',
      render: (v) => eur(v)
    },
    {
      title: 'Margen',
      dataIndex: 'margin',
      key: 'margin',
      align: 'right',
      render: (v, r) => (
        <span style={{ color: v >= 0 ? '#237804' : '#a8071a', fontWeight: 600 }}>
          {eur(v)} <span style={{ fontWeight: 400, fontSize: 12 }}>({marginPct(v, r.retailRcs)})</span>
        </span>
      ),
      sorter: (a, b) => a.margin - b.margin
    },
    {
      title: '',
      key: 'detail',
      render: (_, r) => (
        <Button size="small" onClick={() => setDetailUser(r)}>
          Ver detalle
        </Button>
      )
    }
  ]

  const onSaveInvoice = async (invoice) => {
    const res = await saveInvoice(invoice)
    setInvoices(res.invoices || {})
  }

  const t = totals
  const month = fullMonthOf(applied)
  const invoice = month ? invoices[month] : null
  const pf = (t && t.phoneFunnel) || {}
  const f = (t && t.funnel) || {}
  const costSegments = t
    ? [
        { key: 'conversacion', label: 'Conversación', v: (t.firstSessions || 0) * rates.conversacional, color: COLORS.conversacion },
        { key: 'extra', label: 'Sesión extra', v: (t.extraSessions || 0) * rates.conversacional, color: COLORS.extra },
        { key: 'simple', label: 'Simple', v: t.simple * rates.simple, color: COLORS.simple },
        { key: 'basico', label: 'Básico', v: t.basico * rates.basico, color: COLORS.basico }
      ]
    : []

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 4
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          Facturación Orange (coste vs cobrado)
        </h1>
        <BillingFilters
          draft={draft}
          applied={applied}
          onDraft={setDraft}
          onApply={() =>
            draft.from !== applied.from || draft.to !== applied.to ? loadBilling(draft) : loadBilling(applied, true)
          }
          loading={loading}
        />
      </div>
      <div style={{ color: '#8c8c8c', marginBottom: 12, fontSize: 13 }}>
        {loading
          ? `Calculando ${dmy(draft.from)} – ${dmy(draft.to)}…`
          : computedAt
          ? `Mostrando ${dmy(applied.from)} – ${dmy(applied.to)} · datos del ${computedAt.toLocaleDateString('es-ES')} a las ${computedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`
          : ''}
        {' · '}Coste calculado con las tres tarifas de Orange; en agosto cuadra con la factura al +0,01 %.
      </div>

      <Glossary />

      {error && !t && (
        <Alert type="error" showIcon message="No se pudo cargar la facturación" style={{ marginBottom: 16 }} />
      )}

      <div style={{ position: 'relative' }}>
        {loading && (
          <div style={{ position: 'sticky', top: 12, zIndex: 10, height: 0, display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                height: 40,
                padding: '0 18px',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 999,
                boxShadow: '0 6px 20px rgba(15, 23, 42, 0.12)',
                fontSize: 13
              }}
            >
              <Spin size="small" />
              Calculando {dmy(draft.from)} – {dmy(draft.to)}…
            </div>
          </div>
        )}
        {!t && loading && (
          <Card style={{ marginTop: 56 }}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
        )}
        <div
          style={{
            opacity: loading ? 0.35 : 1,
            transition: 'opacity 0.2s',
            pointerEvents: loading ? 'none' : undefined
          }}
        >
          {t && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                <Kpi
                  label="Coste Orange"
                  value={eur(t.cost)}
                  color="#b45309"
                  sub={invoice ? `Factura de Orange: ${eur(invoice.total)}` : 'Calculado'}
                />
                <Kpi
                  label="Cobrado al cliente (RCS)"
                  value={eur(t.retailRcs)}
                  sub={`${num(t.rcsInitSubmitted)} envíos · ${num(t.rcsInitUnits)} cobrados por RCS${
                    t.rcsInitSubmitted > t.rcsInitUnits
                      ? ` y ${num(t.rcsInitSubmitted - t.rcsInitUnits)} por SMS (no les llegó el RCS)`
                      : ''
                  } · además SMS ${eur(t.retailSms || 0)}`}
                />
                <Kpi
                  label="Margen RCS"
                  value={`${eur(t.margin)}`}
                  sub={marginPct(t.margin, t.retailRcs)}
                  color={t.margin >= 0 ? '#237804' : '#a8071a'}
                />
                <Kpi
                  label="Teléfonos contactados"
                  value={num(pf.contacted)}
                  sub={`${num(pf.interacted)} interactuaron`}
                />
                <Kpi
                  label="Sesiones cobradas"
                  value={num(t.sessions)}
                  sub={`${num(t.firstSessions)} conversaciones + ${num(t.extraSessions)} extra`}
                />
              </div>

              <MoneyFlow
                totals={t}
                rates={rates}
                invoice={invoice}
                invoiceOk={!!invoice && Math.abs((t.cost - invoice.total) / invoice.total) <= 0.05}
              />

              {month && (
                <ReconciliationCard
                  month={month}
                  totals={t}
                  rates={rates}
                  invoice={invoice}
                  onSave={onSaveInvoice}
                />
              )}

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <Card size="small" title="Qué se cobra" style={{ flex: '2 1 520px', minWidth: 0 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <ChargeBreakdown totals={t} rates={rates} />
                  </div>
                </Card>
                <Card size="small" title="Reparto del coste" style={{ flex: '1 1 300px' }}>
                  <Donut segments={costSegments} />
                </Card>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: 16,
                  marginBottom: 20
                }}
              >
                <Card size="small" title="Embudo por teléfono">
                  <Funnel
                    steps={[
                      { label: 'Contactados', v: pf.contacted || 0, c: '#334155' },
                      { label: 'Les llegó el mensaje', v: pf.delivered || 0, c: COLORS.simple },
                      { label: 'Lo vieron', v: pf.displayed || 0, c: '#6366F1' },
                      { label: 'Interactuaron (pulsan o escriben)', v: pf.interacted || 0, c: COLORS.incluido }
                    ]}
                    note={`Teléfonos distintos con mensaje inicial en el periodo. Ver el mensaje no se cobra; interactuar abre una conversación.${
                      t.firstSessions > pf.interacted
                        ? ` Las ${num(t.firstSessions)} conversaciones cobradas corresponden a ${num(pf.interacted)} teléfonos: algunos conversaron en más de una campaña.`
                        : ''
                    }`}
                  />
                </Card>
                <Card size="small" title="Embudo por mensaje">
                  <Funnel
                    steps={[
                      { label: 'Enviados (aceptados por Orange)', v: f.sent || 0, c: '#334155' },
                      { label: 'Entregados', v: f.delivered || 0, c: COLORS.simple },
                      { label: 'Vistos', v: f.displayed || 0, c: '#6366F1' },
                      { label: 'Fallidos (no entregados)', v: f.failed || 0, c: COLORS.fallido }
                    ]}
                    note="Todos los mensajes, incluidas las respuestas del flujo. Orange cobra sobre el enviado."
                  />
                </Card>
              </div>

              {t.daily && t.daily.length > 0 && (
                <Card size="small" title="Coste y sesiones por día" style={{ marginBottom: 20 }}>
                  <DailyChart daily={t.daily} />
                </Card>
              )}

              <Card size="small" title="Por cliente" style={{ marginBottom: 20 }}>
                <Table
                  rowKey="userId"
                  columns={columns}
                  dataSource={rows}
                  pagination={false}
                  scroll={{ x: 1100 }}
                  size="middle"
                  onRow={(r) => ({ onClick: () => setDetailUser(r), style: { cursor: 'pointer' } })}
                />
              </Card>
            </>
          )}
        </div>
      </div>

      {ratesDraft && (
        <Card
          title="Tarifas de coste Orange (€ por evento) — solo admin"
          size="small"
          style={{ marginTop: 24, maxWidth: 640 }}
        >
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Básico</div>
              <InputNumber
                step={0.001}
                min={0}
                value={ratesDraft.basico}
                onChange={(v) => setRatesDraft({ ...ratesDraft, basico: v })}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Simple</div>
              <InputNumber
                step={0.001}
                min={0}
                value={ratesDraft.simple}
                onChange={(v) => setRatesDraft({ ...ratesDraft, simple: v })}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Conversacional (sesión)</div>
              <InputNumber
                step={0.001}
                min={0}
                value={ratesDraft.conversacional}
                onChange={(v) => setRatesDraft({ ...ratesDraft, conversacional: v })}
              />
            </div>
            <Button type="primary" loading={savingRates} onClick={onSaveRates}>
              Guardar tarifas
            </Button>
          </div>
          {rates.updatedAt && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 10 }}>
              Última actualización: {new Date(rates.updatedAt).toLocaleString('es-ES')}
              {rates.updatedBy ? ` · ${rates.updatedBy}` : ''}
            </div>
          )}
        </Card>
      )}

      <UserDetailDrawer user={detailUser} range={applied} onClose={() => setDetailUser(null)} />
    </div>
  )
}

export default Billing
